/**
 * Avenged Sevenfold — Life Is But a Dream… (2023)
 *
 * Source of truth: tmp drop `a7x_libad.json` (deep_dive.content) and the
 * track listing inside the same payload. Every analytical paragraph below
 * is verbatim from that JSON; the only derivations are:
 *
 *   - hero / deepDiveLine / sectionTitle: decomposed from `form_behavior_gap`
 *     ("Parece X. Se mueve como Y...") into the apparent / key / coda parts
 *     the schema asks for. No paraphrasing.
 *   - position_label: stitched from `discography_position.position_claim`
 *     and the first clause of `discography_position.summary`.
 *   - signature_moments[].pull / .why: the JSON ships a single
 *     `why_it_matters` string per moment; we split it at the first sentence
 *     boundary so `pull` carries the thesis and `why` carries the rest.
 *     Same heuristic we already use for movement framing in DeepDive.tsx.
 *   - tensions[].anchors: the JSON ships strings of the form
 *     `"<Track>": <note>`. We parse `track` and `note` out, preserving the
 *     original wording on both sides.
 *
 * `next_album` / `next_context` are intentionally absent: this is currently
 * the latest A7X studio album, so there is no successor to talk about.
 * Schema accepts these as optional.
 */

import type { AlbumLibraryEntry, DeepDive } from '@/types/album';

export const library = {
  id: 'life-is-but-a-dream',
  artist: 'Avenged Sevenfold',
  album: 'Life Is But a Dream…',
  year: 2023,
  genre: 'Metal progresivo',
  hook: 'Una crisis existencial fragmentada que usa el metal sólo como una de varias máscaras posibles.',
  palette: { bg: '#0E1116', fg: '#D8D0C2', accent: '#8A93B8' },
  typeStyle: 'bold-serif',
  available: true,
  hero: {
    apparent: 'una declaración prog-metal gigantesca',
    key: 'una crisis existencial fragmentada',
    coda: 'que usa el metal sólo como una de varias máscaras posibles.',
  },
  deepDiveLine: {
    apparent: 'una declaración prog-metal gigantesca',
    key: 'una crisis existencial fragmentada que usa el metal sólo como una de varias máscaras posibles',
  },
  sectionTitle: {
    pre: 'Life Is But a Dream…, leído como una',
    em: 'crisis existencial fragmentada',
    post: 'que usa el metal sólo como una de varias máscaras posibles.',
  },
  songsTitle: 'Tres canciones, leídas con cuidado.',
  footerCredit: 'Análisis original · Life Is But a Dream… · Avenged Sevenfold · 2023',
  coverImage: '/images/libad.jpg',
} as const satisfies AlbumLibraryEntry;

export const deepDive: DeepDive = {
  artist: 'Avenged Sevenfold',
  title: 'Life Is But a Dream…',
  year: '2023',
  position_label: 'Octavo álbum de estudio · Ruptura tardía deliberada',
  apparent_form:
    'Parece un álbum de metal progresivo de regreso tardío, grande, conceptual y muy consciente de sí mismo.',
  actual_behavior:
    'En secuencia, el disco se comporta como una desintegración controlada. Arranca con ansiedad física y sátira de consumo, encuentra un centro extrañamente tierno en "Cosmic", y luego se mete en un mini-suite final donde la banda cambia de idioma musical varias veces hasta terminar en una pieza de piano que suena como si el álbum se hubiera quedado sin cuerpo.',
  form_behavior_gap:
    'Parece una declaración prog-metal gigantesca. Se mueve como una crisis existencial fragmentada que usa el metal sólo como una de varias máscaras posibles.',
  previous_context:
    '"The Stage" ya había empujado a la banda hacia un prog-metal más amplio y cósmico, con canciones largas y ambición conceptual; este disco toma esa apertura y la vuelve mucho menos estable, más teatral y más dispuesto a romper su propia identidad.',
  previous_album: { title: 'The Stage', year: 2016 },
  // No next album — Life Is But a Dream… is the latest A7X studio LP.
  position_summary:
    'Octavo álbum de estudio y el gesto más rupturista de su catálogo principal. Después de años afinando distintas versiones de grandeza metal, aquí la banda usa su oficio para desmontar su propia imagen y probar cuánto de Avenged Sevenfold puede sobrevivir fuera de sus hábitos normales.',

  arch_type: 'Bisagra emocional',
  arch_summary:
    'El álbum avanza por bloques de presión que encuentran un eje emocional a mitad de camino y luego se rehúsan a cerrarse de forma convencional. Más que subir de manera lineal, gira sobre un punto de revelación y termina desmaterializándose.',
  arch_opening:
    '"Game Over" y "Mattel" abren con la impresión de un disco agresivo, nervioso y satírico, casi una versión futurista del músculo clásico de la banda. Señalan bien la ansiedad del álbum, pero esconden algo central: la mitad final va a abandonar ese marco y tratar el colapso con mucha más extrañeza que pura fuerza.',
  arch_middle:
    'El centro real está entre "Nobody" y "Cosmic". "Nobody" fija la tesis de disolución con un tono sorprendentemente sereno, y "Cosmic" abre la cápsula emocional más grande del disco, la única zona donde la banda deja de sonar cínica durante varios minutos. "Beautiful Morning" cae después como una resaca espesa, y ese bajón prepara el terreno para el tramo final, que ya no busca continuidad sino metamorfosis.',
  arch_closing:
    'El cierre funciona como una evaporación. El tríptico "G" / "(O)rdinary" / "(D)eath" saca a la banda del metal de forma cada vez más extrema y deja a "Life Is but a Dream…" como una coda desnuda, sin red, donde la única resolución posible es seguir tocando mientras todo lo demás desaparece.',
  arch_sequencing_note:
    'La secuencia reserva su mutación más radical para los últimos cuatro cortes: primero una caída pesada en "Beautiful Morning", luego una falsa estabilización en "Easier", y después el tríptico "GOD" como salida de emergencia estilística.',

  tensions: [
    {
      label: 'Seguir funcionando vs aceptar el derrumbe',
      description:
        'Gran parte del disco vive entre el impulso de seguir acelerando y la certeza de que esa aceleración ya vació todo. "Game Over" empuja con riffs nerviosos y cambios bruscos como si vivir fuera una carrera absurda; "We Love You" convierte esa lógica en una orden industrial. Cuando llega "(D)eath)", el tono ya no pelea: se deja llevar con una calma rara, como si el agotamiento hubiera ganado.',
      anchors: [
        { track: 'Game Over', note: 'arranque frenético seguido por la caída del cuerpo' },
        {
          track: 'We Love You',
          note: 'mandato repetido de producir, consumir y multiplicarse',
        },
        {
          track: '(D)eath',
          note: 'la voz se vuelve casi de crooner mientras canta sobre el final',
        },
      ],
    },
    {
      label: 'Querer trascender el yo vs descubrir que sin yo también hay vacío',
      description:
        'El disco quiere escapar del ego, pero también teme quedarse sin una persona real adentro. "Mattel" trata la realidad como mercancía prefabricada y convierte la identidad en otro objeto barato; "Nobody" lleva esa idea mucho más lejos y presenta la desaparición del yo como revelación. El cierre instrumental deja la pregunta abierta: cuando ya no hay máscara ni personaje, queda belleza, pero también un hueco muy concreto.',
      anchors: [
        { track: 'Mattel', note: 'fantasía de plástico, mundo falso y yo desrealizado' },
        {
          track: 'Nobody',
          note: 'la disolución del yo cantada con serenidad casi mística',
        },
        {
          track: 'Life Is but a Dream…',
          note: 'cierre sin voz, sólo una figura de piano expuesta',
        },
      ],
    },
    {
      label: 'Querer conectar de forma inmediata vs sabotear cualquier comodidad',
      description:
        'Avenged Sevenfold siempre tuvo instinto para el exceso grande y pegajoso, pero aquí usa ese instinto para incomodar. Los ganchos están por todas partes, aunque muchas veces llegan con un gesto torcido o una producción que te hace sentir observado. "(O)rdinary" suena amable hasta que entiendes que esa amabilidad está programada; "G" vuelve la grandilocuencia casi un chiste cósmico.',
      anchors: [
        {
          track: 'We Love You',
          note: 'coros casi publicitarios y secciones violentamente rotas',
        },
        { track: '(O)rdinary', note: 'funk sintético, limpio, pegadizo, casi robótico' },
        { track: 'G', note: 'teatralidad gospel y humor negro sobre juicio y poder' },
      ],
    },
    {
      label: 'Anhelo de trascendencia vs incapacidad para habitarla sin romperla',
      description:
        'El disco sí busca algo parecido a la belleza, pero cada vez que la encuentra la contamina con ironía, náusea o distancia. "Cosmic" ofrece el momento más cercano a una elevación sincera de todo el álbum. Justo por eso pesa tanto lo que sigue: "Beautiful Morning" cae como una mañana posterior al éxtasis, y el tramo final prefiere disfrazarse de teatro, electro-funk y balada mortuoria antes que quedarse demasiado tiempo en esa apertura.',
      anchors: [
        { track: 'Cosmic', note: 'expansión emocional, melodía abierta, sensación de ascenso' },
        {
          track: 'Beautiful Morning',
          note: 'pesadez narcótica y resaca después del pico',
        },
        {
          track: 'G / (O)rdinary / (D)eath',
          note: 'cambio de códigos como fuga del dolor frontal',
        },
      ],
    },
  ],

  signature_moments: [
    {
      track: 'We Love You',
      anchor: 'el cambio desde la avalancha de órdenes hacia el pasaje coral y partido del centro',
      pull: 'Ese quiebre enseña la gramática real del álbum.',
      why: 'La banda puede escribir un tema enorme y directo, pero prefiere romperlo desde adentro para que el placer suene también como coerción. Después de ese momento, cada giro del disco se oye menos como capricho y más como método.',
    },
    {
      track: 'G / (O)rdinary / (D)eath',
      anchor:
        'la entrada de "G" seguida por "(O)rdinary" y "(D)eath" como tríptico continuo antes del piano final',
      pull: 'Ahí se entiende que el cierre no busca una resolución metal, sino una salida por mutación.',
      why: 'Gospel satírico, funk robótico y balada de club antiguo forman una especie de juicio final de géneros, y preparan el terreno para que la pieza de piano final no parezca un adorno, sino el último resto humano después del teatro.',
    },
  ],

  motifs: [
    'La irrealidad de lo humano aparece en "Mattel", "Nobody", "We Love You" y "(O)rdinary", donde personas, cuerpos y deseos se sienten fabricados o programados.',
    'La muerte y la salida del yo recorren "Game Over", "Nobody", "Cosmic" y "(D)eath", a veces como terror, a veces como alivio, nunca como una respuesta simple.',
    'El lenguaje de lo cósmico o lo total reaparece en "Nobody", "Cosmic" y "G", desplazando los problemas personales hacia una escala casi metafísica.',
  ],

  enduring:
    'Este disco va a durar porque no intenta quedar bien. Llega tarde en la carrera de una banda grande y, en vez de resumir fortalezas conocidas, decide arriesgar prestigio para perseguir una idea incómoda: que la identidad, el éxito, el cuerpo y hasta el estilo musical pueden ser disfraces temporales. Por eso sigue generando resistencia y devoción a la vez. Cuando conecta, conecta muy hondo, porque convierte la extravagancia en una forma bastante honesta de hablar del miedo a existir.',

  tracks: [
    { n: 1, title: 'Game Over', ms: 226066, hasMeaning: true },
    { n: 2, title: 'Mattel', ms: 330000 },
    { n: 3, title: 'Nobody', ms: 353986, hasMeaning: true },
    { n: 4, title: 'We Love You', ms: 375000 },
    { n: 5, title: 'Cosmic', ms: 451000, hasMeaning: true, hinge: true },
    { n: 6, title: 'Beautiful Morning', ms: 392000 },
    { n: 7, title: 'Easier', ms: 217760 },
    { n: 8, title: 'G', ms: 217773 },
    { n: 9, title: '(O)rdinary', ms: 172000 },
    { n: 10, title: '(D)eath', ms: 199453 },
    { n: 11, title: 'Life Is but a Dream…', ms: 271933 },
  ],

  songs_with_meaning: ['Game Over', 'Nobody', 'Cosmic'],
};
